<script lang="ts">
	import DeliveryCountrySelect from "@components/DeliveryCountrySelect.svelte";
	import PPLDeliveryPicker from "@components/PPLDeliveryPicker.svelte";
	import CartRadioOption from "@components/cart/CartRadioOption.svelte";
	import CurrencySwitcher from "@components/cart/CurrencySwitcher.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Form from "@components/form/Form.svelte";
	import RadioGroup from "@components/form/RadioGroup.svelte";
	import CartLayout from "@components/layout/CartLayout.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { clarityEvent, clarityUpgrade } from "@utils/clarity.js";
	import { getFormFields } from "@utils/getFormFields.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount, untrack } from "svelte";
	import { type DetailResponseModel, getDeliveryTypeFromPplResponse } from "@core/components/PPLAccessPoint.js";
	import type { Address } from "@core/schema/Address.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { Country } from "@core/schema/Country.js";
	import { DeliveryType } from "@core/schema/DeliveryType.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { PaymentInput } from "@core/schema/Payment.js";
	import { PaymentType } from "@core/schema/PaymentType.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { deliveryTypeNameMap } from "@core/schema/names/deliveryTypeNameMap.js";
	import { paymentTypeNameMap } from "@core/schema/names/paymentTypeNameMap.js";
	import { personalPickupAddress } from "@core/schema/personalPickupAddress.js";
	import { calculateOrderDiscounts } from "@core/schema/utils/calculateOrderDiscounts.js";
	import { calculatePriceOfDelivery } from "@core/schema/utils/calculatePriceOfDelivery.js";
	import { countryToIsoCodeMap } from "@core/schema/utils/countryToIsoCodeMap.js";
	import { formatAddress } from "@core/schema/utils/formatAddress.js";
	import { getDeliveryTypesForCountry } from "@core/schema/utils/getDeliveryTypesForCountry.js";
	import { getShippingStartsAtOfProducts } from "@core/schema/utils/getShippingStartsAtOfProducts.js";
	import { getSortedPaymentTypesForCustomer } from "@core/schema/utils/getSortedPaymentTypesForCustomer.js";
	import { isCountryInEuropeanUnion } from "@core/schema/utils/isCountryInEuropeanUnion.js";
	import { paymentTypeToGoPayPaymentInstrumentMap } from "@core/schema/utils/paymentTypeToGoPayPaymentInstrumentMap.js";
	import { shouldApplyTax } from "@core/schema/utils/shouldApplyTax.js";
	import { deepMerge } from "@core/utils/deepMerge.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { isPplDeliveryType } from "@core/utils/isPplDeliveryType.js";
	import { oneTimeEffect } from "@core/utils/svelte/oneTimeEffect.svelte.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { reportCheckoutBegan } from "../UniversalAnalytics.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import type { CartFieldsForStep } from "../cart/CartType.js";
	import { defaultDestinationCountry } from "../defaultDestinationCountry.js";
	import { selectedDestinationCountry } from "../state/selectedDestinationCountry.svelte.js";
	import translations from "./CartPaymentDelivery.translations.json" with { type: "json" };
	import type { CartPaymentDeliveryProps } from "./CartPaymentDeliveryProps.js";

	const { tenant, countrySettings, exchangeRatesSetting, subcategories }: CartPaymentDeliveryProps = $props();
	const translate = getTranslate(translations);

	const currentStep = CartStep.PaymentDelivery;
	type FieldsForStep = CartFieldsForStep<typeof currentStep>;
	const cartInStep = $derived(Cart.getCorrectCartInStepOrRedirect(tenant, currentStep).cartInStep);
	const preorderShippingStartsAt = $derived(getShippingStartsAtOfProducts(Cart.productsInOrder, Date.now()));
	const preorderNoteKey = $derived(
		cartInStep.order.deliveryType === DeliveryType.PersonalPickup ? "preorderPickupNote" : "preorderShippingNote",
	);
	let form = $state<HTMLFormElement>();
	let pplBoxSupportsCod = $state(true);

	const paymentMethods = $derived.by(() => {
		if (cartInStep.hasSubscription && cartInStep.subscription.kind === SubscriptionKind.Recurring) {
			return [PaymentType.Subscription];
		}
		if (cartInStep.hasCourseOrder) {
			return Object.values(PaymentType).filter((type) => paymentTypeToGoPayPaymentInstrumentMap[type] !== null);
		}
		const methods = getSortedPaymentTypesForCustomer(
			Auth.loggedCustomer,
			cartInStep.order.deliveryAddress?.country ?? defaultDestinationCountry,
			Cart.currency ?? tenant.defaultCurrency,
		);
		if (cartInStep.hasSubscription && cartInStep.subscription.kind === SubscriptionKind.Gift) {
			return methods.filter((type) => type !== PaymentType.OnDelivery);
		}
		if (isPplDeliveryType(cartInStep.order.deliveryType) && !pplBoxSupportsCod) {
			return methods.filter((type) => type !== PaymentType.OnDelivery);
		}
		return methods;
	});
	const visibleBeforeShowMore = 2;
	const selectedInInvisible = $derived(
		cartInStep.order.payment?.type !== undefined &&
			paymentMethods.indexOf(cartInStep.order.payment.type) > visibleBeforeShowMore,
	);
	let showMorePaymentMethods = $state(false);
	$effect(() => {
		if (selectedInInvisible && !showMorePaymentMethods) {
			showMorePaymentMethods = true;
		}
	});
	$effect(() => {
		// The selected access point may not support COD, which drops OnDelivery from paymentMethods.
		if (cartInStep.order.payment?.type !== undefined && !paymentMethods.includes(cartInStep.order.payment.type)) {
			Cart.saveOrderFields({ payment: { type: undefined } });
		}
	});

	function toggleShowMorePaymentMethods(): void {
		showMorePaymentMethods = !showMorePaymentMethods;
	}

	let deliveryAddress = $state<Address>({
		country:
			(selectedDestinationCountry.current === "outsideEu"
				? (untrack(() => countrySettings).find(({ country }) => !isCountryInEuropeanUnion(country))?.country ??
					Country.Czechia)
				: selectedDestinationCountry.current) ?? defaultDestinationCountry,
		firstName: "",
		lastName: "",
		city: "",
		street: "",
		zip: "",
	});

	function getFields(form: HTMLFormElement): FieldsForStep {
		const { deliveryType, paymentMethod } = getFormFields(form);

		if (!Cart.price) {
			throw new Error("Cart price is not available");
		}

		return {
			deliveryAddress: $state.snapshot(deliveryAddress),
			deliveryType: deliveryType as DeliveryType,
			payment: { type: paymentMethod } as PaymentInput,
		};
	}

	function submitForm(_event: SubmitEvent, form: HTMLFormElement): void {
		const fields = getFields(form);
		Cart.advanceToNextStep(currentStep, fields);
	}

	function save(event: Event & { currentTarget: HTMLFormElement }): void {
		Cart.saveOrderFields(getFields(event.currentTarget));
	}

	$effect(() => {
		// Course orders skip the delivery-address step entirely - the place of supply is always Czechia.
		if (cartInStep.hasCourseOrder && deliveryAddress.country !== Country.Czechia) {
			deliveryAddress = { ...deliveryAddress, country: Country.Czechia };
			Cart.saveOrderFields({ deliveryAddress });
		}
	});

	oneTimeEffect(
		() => Cart.loaded,
		() => {
			deliveryAddress = deepMerge<Address>(
				deliveryAddress,
				cartInStep.order.billingAddress ?? {},
				Auth.loggedCustomer?.deliveryAddresses[0] ?? {},
				cartInStep.order.deliveryAddress ?? {},
			);
		},
	);

	oneTimeEffect(
		() => Cart.loaded && Boolean(Cart.price),
		() => {
			if (!Cart.price) {
				return;
			}
			reportCheckoutBegan(cartInStep.order, Cart.price, tenant.locale, {
				applyTax: shouldApplyTax(deliveryAddress.country, Cart.vatType ?? EconomicSubjectVatType.NotVatSubject),
			});
		},
	);

	onMount(() => {
		clarityUpgrade("checkout");
		clarityEvent("cartStepPaymentDelivery");
	});

	let pplDeliveryPicker = $state<PPLDeliveryPicker>();
	const deliveryTypes = $derived(
		cartInStep.hasCourseOrder
			? []
			: getDeliveryTypesForCountry(deliveryAddress.country).filter(
					(type) => !cartInStep.hasSubscription || type !== DeliveryType.PersonalPickup,
				),
	);
	// PPL parcel shop and parcel box are merged into a single option, shown separately above the other delivery types.
	const nonPplDeliveryTypes = $derived(deliveryTypes.filter((type) => !isPplDeliveryType(type)));
	const pplDeliveryAvailable = $derived(deliveryTypes.some((type) => isPplDeliveryType(type)));

	const freeDelivery = $derived.by(() => {
		const { deliveryAddress } = cartInStep.order;
		if (deliveryAddress?.country === undefined) {
			return false;
		}
		if (cartInStep.hasSubscription || cartInStep.hasCourseOrder) {
			return true;
		}
		const { freeDelivery } = calculateOrderDiscounts(
			{
				...cartInStep.order,
				deliveryAddress: {
					city: deliveryAddress.city ?? "",
					firstName: deliveryAddress.firstName ?? "",
					lastName: deliveryAddress.lastName ?? "",
					street: deliveryAddress.street ?? "",
					zip: deliveryAddress.zip ?? "",
					state: deliveryAddress.state,
					pplDeliveryPoint: undefined,
					country: deliveryAddress.country,
				},
			},
			{ customer: Auth.loggedCustomer, countrySettings, subcategories, coupons: Cart.coupons },
		);
		return freeDelivery;
	});
</script>

<CartLayout {currentStep} selectedCountry={deliveryAddress.country}>
	{#if Cart.loaded && Cart.currency !== undefined}
		<Form class="col-span-full lg:col-span-3" onchange={save} onsubmit={submitForm} bind:form>
			<Heading level={1} text={translate("paymentDeliveryTitle")} class="font-antipol font-medium text-10/tighter" />
			<div class="mt-8 mb-4 flex flex-col gap-12">
				{#if !cartInStep.hasCourseOrder}
					<div class="flex flex-col gap-4">
						<Heading level={2} text={translate("shippingCountryLabel")} class="font-medium text-fcrgray" />
						<DeliveryCountrySelect
							name="deliveryCountry"
							bind:value={deliveryAddress.country}
							required
							restrictToCountries={Cart.restrictToCountries}
							onchange={(): void => {
								// PPL delivery type & personal pickup is country dependent, so reset it when changing country
								if (
									isPplDeliveryType(cartInStep.order.deliveryType) ||
									cartInStep.order.deliveryType === DeliveryType.PersonalPickup
								) {
									Cart.saveOrderFields({ deliveryType: undefined });
								}
								// OnDelivery payment type is country dependent, so reset it when changing country
								if (cartInStep.order.payment?.type === PaymentType.OnDelivery) {
									Cart.saveOrderFields({ payment: { type: undefined } });
								}
								// Reset local state and cart delivery address when changing country — must clear
								// both because the form's onchange=save fires after this and would re-save local state.
								pplBoxSupportsCod = true;
								deliveryAddress = {
									...deliveryAddress,
									city: "",
									firstName: "",
									lastName: "",
									street: "",
									zip: "",
									state: undefined,
									pplDeliveryPoint: undefined,
								};
								Cart.saveOrderFields({ deliveryAddress });
							}}
						/>
					</div>
				{/if}
				<div class="flex flex-col gap-6">
					<Heading level={2} text={translate("orderCurrency")} class="font-antipol font-medium text-5.5/tighter" />
					<CurrencySwitcher huge />
					{#if preorderShippingStartsAt}
						<p class="text-fcrgray text-4">
							<Formatted
								text={translate(preorderNoteKey, {
									shippingDate: toDateMonthYearFormat(preorderShippingStartsAt, tenant.locale),
								})}
							/>
						</p>
					{/if}
					{#if !cartInStep.hasCourseOrder}
						<Heading
							level={2}
							text={translate("shippingMethodTitle")}
							class="font-antipol font-medium text-5.5/tighter"
						/>
						{#if pplDeliveryAvailable}
							{@const pplPrice =
								Cart.destinationCountry !== undefined && Cart.state && !freeDelivery
									? calculatePriceOfDelivery(Cart.destinationCountry, DeliveryType.DeliveryToParcelShop, {
											applyTax: shouldApplyTax(
												Cart.destinationCountry,
												Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
											),
											countrySettings,
											currency: Cart.currency,
											customer: Auth.loggedCustomer,
											exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
											vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
										}).withTax
									: 0}
							{@const pplPriceFormatted =
								pplPrice === 0 ? translate("forFree") : formatPrice(pplPrice, Cart.currency, tenant.locale, false)}
							{@const pplSelectedType = isPplDeliveryType(cartInStep.order.deliveryType)
								? cartInStep.order.deliveryType
								: DeliveryType.DeliveryToParcelShop}
							<CartRadioOption
								name="deliveryType"
								text={translate("pplDeliveryMergedLabel")}
								note={isPplDeliveryType(cartInStep.order.deliveryType)
									? deliveryAddress.pplDeliveryPoint?.name
									: undefined}
								value={pplSelectedType}
								group={cartInStep.order.deliveryType}
								onclick={async (event: MouseEvent): Promise<void> => {
									if (!pplDeliveryPicker) {
										throw new Error("PPL delivery picker is not available");
									}
									event.preventDefault();
									const country = deliveryAddress.country === Country.Slovakia ? Country.Slovakia : Country.Czechia;
									// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
									const result: DetailResponseModel | undefined = await pplDeliveryPicker.pick({
										country: countryToIsoCodeMap[country],
										codRequired: cartInStep.order.payment?.type === PaymentType.OnDelivery,
										packageWeight: Cart.weightInGrams / 1000 || undefined,
									});
									if (!result) {
										return;
									}
									pplBoxSupportsCod = !result.paymentMethods?.onlineRequired;
									deliveryAddress = {
										...deliveryAddress,
										city: result.address?.city ?? "",
										zip: result.address?.zipCode ?? "",
										street: result.address?.street ?? "",
										country,
										pplDeliveryPoint: {
											code: result.code,
											name: result.name,
										},
									};
									Cart.saveOrderFields({
										deliveryType: getDeliveryTypeFromPplResponse(result),
										deliveryAddress,
									});
								}}
								priceFormatted={pplPriceFormatted}
							/>
						{/if}
						<RadioGroup wrapperClass="flex flex-col gap-4" required value={cartInStep.order.deliveryType}>
							{#each nonPplDeliveryTypes as deliveryType (deliveryType)}
								{@const price =
									Cart.destinationCountry !== undefined && Cart.state && !freeDelivery
										? calculatePriceOfDelivery(Cart.destinationCountry, deliveryType, {
												applyTax: shouldApplyTax(
													Cart.destinationCountry,
													Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
												),
												countrySettings,
												currency: Cart.currency,
												customer: Auth.loggedCustomer,
												exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
												vatType: Cart.vatType ?? EconomicSubjectVatType.NotVatSubject,
											}).withTax
										: 0}
								{@const priceFormatted =
									price === 0 ? translate("forFree") : formatPrice(price, Cart.currency, tenant.locale, false)}
								<CartRadioOption
									name="deliveryType"
									text={deliveryTypeNameMap[deliveryType][tenant.locale]}
									note={deliveryType === cartInStep.order.deliveryType
										? deliveryType === DeliveryType.PersonalPickup
											? formatAddress(personalPickupAddress, {
													includeName: false,
													includeCountry: false,
													separator: ", ",
													locale: tenant.locale,
												})
											: undefined
										: undefined}
									value={deliveryType}
									group={cartInStep.order.deliveryType}
									onclick={(): void => {
										if (deliveryType === DeliveryType.PersonalPickup) {
											const { firstName, lastName, pplDeliveryPoint } = deliveryAddress;
											deliveryAddress = {
												...personalPickupAddress,
												pplDeliveryPoint,
												firstName,
												lastName,
											};
										} else if (cartInStep.order.deliveryType !== deliveryType) {
											deliveryAddress = {
												...deliveryAddress,
												state: undefined,
												firstName: "",
												lastName: "",
												city: "",
												zip: "",
												street: "",
											};
										}
										Cart.saveOrderFields({ deliveryType, deliveryAddress });
									}}
									{priceFormatted}
								/>
							{/each}
						</RadioGroup>
					{/if}
				</div>
				<div class="flex flex-col gap-6">
					<Heading level={2} text={translate("paymentMethodTitle")} class="font-antipol font-medium text-5.5/tighter" />

					<RadioGroup wrapperClass="flex flex-col gap-4" required value={cartInStep.order.payment?.type}>
						{#each paymentMethods as paymentMethod, index (paymentMethod)}
							<CartRadioOption
								name="paymentMethod"
								text={paymentMethod === PaymentType.Subscription
									? "GoPay"
									: paymentTypeNameMap[paymentMethod][tenant.locale]}
								value={paymentMethod}
								group={cartInStep.order.payment?.type}
								priceFormatted={translate(paymentMethod === PaymentType.Subscription ? "recurring" : "forFree")}
								onchange={(): void => {
									Cart.saveOrderFields({ payment: { type: paymentMethod } });
								}}
								class={[!showMorePaymentMethods && index > visibleBeforeShowMore && "hidden"]}
							/>
						{/each}
						<!-- TODO: Change style of button when designer design the button (if they do) -->
						{#if (!selectedInInvisible || !showMorePaymentMethods) && paymentMethods.length > visibleBeforeShowMore}
							<button onclick={toggleShowMorePaymentMethods} type="button">
								{translate(showMorePaymentMethods ? "showLess" : "showMore")}
							</button>
						{/if}
					</RadioGroup>
					{#if cartInStep.hasSubscription}
						<div class="text-xs/snug text-fcrgray">
							<Formatted text={translate("paymentSecurity")} />
						</div>
					{/if}
				</div>
			</div>

			{#if Cart.itemCount > 0}
				<div
					class="fixed sm:sticky bottom-0 py-3 px-4 sm:px-0 bg-white outline outline-white inset-x-0 shadow-black/8 shadow-[0_-4px_3rem] sm:shadow-none z-20"
				>
					<Button type="submit" variant={ButtonVariant.Dark} size={ButtonSize.Large} class="w-full">
						<div class="hidden sm:block">
							<Formatted text={translate("nextButtonText")} />
						</div>
						<div class="sm:hidden">
							<Formatted text={translate("nextButtonTextMobile")} />
						</div>
					</Button>
				</div>
			{/if}
		</Form>
		<PPLDeliveryPicker
			bind:this={pplDeliveryPicker}
			code={deliveryAddress.pplDeliveryPoint?.code}
			title={translate("pplDeliveryPickerTitle")}
		/>
	{/if}
</CartLayout>
